import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatINR } from '../../../utils/currency';

const AssetAgeHistogram = ({ assets = [], categories = [] }) => {
  const [showDepreciation, setShowDepreciation] = useState(false);

  // Map category IDs to names
  const categoriesMap = useMemo(() => {
    const map = {};
    categories.forEach(cat => {
      map[cat.id] = cat.name;
    });
    return map;
  }, [categories]);

  // Process age data dynamically from assets
  const ageData = useMemo(() => {
    const now = new Date();
    const ageRanges = [
      { label: '0-1 Years', min: 0, max: 1, color: '#059669', defaultDep: 10 },
      { label: '1-2 Years', min: 1, max: 2, color: '#0EA5E9', defaultDep: 25 },
      { label: '2-3 Years', min: 2, max: 3, color: '#D97706', defaultDep: 45 },
      { label: '3-4 Years', min: 3, max: 4, color: '#F97316', defaultDep: 65 },
      { label: '4-5 Years', min: 4, max: 5, color: '#7C3AED', defaultDep: 80 },
      { label: '5+ Years', min: 5, max: Infinity, color: '#DC2626', defaultDep: 95 }
    ];

    const ranges = ageRanges.map(range => ({
      ageRange: range.label,
      count: 0,
      value: 0,
      depreciation: range.defaultDep,
      priority: range.min < 2 ? 'low' : range.min < 3 ? 'medium' : range.min < 4 ? 'high' : 'critical',
      color: range.color,
      categoryCounts: {}
    }));

    assets.forEach(asset => {
      // If no purchase date, estimate from created_at or default to 0-1 years
      const dateStr = asset.purchase_date || asset.created_at;
      if (!dateStr) return;
      
      const purchaseDate = new Date(dateStr);
      if (isNaN(purchaseDate.getTime())) return;

      const yearsDiff = Math.max(0, (now - purchaseDate) / (1000 * 60 * 60 * 24 * 365.25));
      
      let rangeIndex = ranges.length - 1;
      for (let i = 0; i < ranges.length; i++) {
        if (yearsDiff >= ageRanges[i].min && yearsDiff < ageRanges[i].max) {
          rangeIndex = i;
          break;
        }
      }
      
      const price = parseFloat(asset.purchase_price) || parseFloat(asset.price) || 0;
      ranges[rangeIndex].count++;
      ranges[rangeIndex].value += price;
      
      // Categorize dynamically by actual category name
      const catName = categoriesMap[asset.asset_category_id] || asset.category?.name || 'General';
      ranges[rangeIndex].categoryCounts[catName] = (ranges[rangeIndex].categoryCounts[catName] || 0) + 1;
    });

    return ranges;
  }, [assets, categoriesMap]);

  // Calculate dynamic replacement recommendations from real data
  const replacementRecommendations = useMemo(() => {
    const categoryStats = {};
    const now = new Date();

    assets.forEach(asset => {
      const dateStr = asset.purchase_date || asset.created_at;
      if (!dateStr) return;

      const purchaseDate = new Date(dateStr);
      if (isNaN(purchaseDate.getTime())) return;

      const yearsDiff = (now - purchaseDate) / (1000 * 60 * 60 * 24 * 365.25);
      const usefulLife = parseFloat(asset.useful_life_years) || 4;

      // Assets that exceeded their useful life or are older than 4 years
      if (yearsDiff >= usefulLife || yearsDiff >= 4) {
        const catName = categoriesMap[asset.asset_category_id] || asset.category?.name || 'General Assets';
        const cost = parseFloat(asset.purchase_price) || parseFloat(asset.price) || 0;
        
        if (!categoryStats[catName]) {
          categoryStats[catName] = {
            category: catName,
            count: 0,
            estimatedCost: 0,
            avgAgeYears: 0,
            totalAgeYears: 0
          };
        }
        categoryStats[catName].count++;
        categoryStats[catName].estimatedCost += cost;
        categoryStats[catName].totalAgeYears += yearsDiff;
      }
    });

    return Object.values(categoryStats).map((item, index) => {
      const avgAge = (item.totalAgeYears / (item.count || 1)).toFixed(1);
      return {
        id: `REC-${index + 1}`,
        category: item.category,
        count: item.count,
        estimatedCost: item.estimatedCost,
        urgency: item.count >= 5 ? 'critical' : item.count >= 3 ? 'high' : 'medium',
        reason: `Averaging ${avgAge} years in operation. Exceeds standard replacement lifecycle; consider renewal to avoid downtime.`,
        ageRange: `${avgAge} yrs avg`
      };
    }).sort((a, b) => b.count - a.count);
  }, [assets, categoriesMap]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-4 shadow-elevated">
          <p className="font-semibold text-popover-foreground mb-2">{label}</p>
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between space-x-4">
              <span className="text-muted-foreground">Asset Count:</span>
              <span className="font-bold text-popover-foreground">{data?.count}</span>
            </div>
            <div className="flex justify-between space-x-4">
              <span className="text-muted-foreground">Total Book Value:</span>
              <span className="font-bold text-success">{formatINR(data?.value, false)}</span>
            </div>
            <div className="flex justify-between space-x-4">
              <span className="text-muted-foreground">Avg Depreciation:</span>
              <span className="font-bold text-warning">{data?.depreciation}%</span>
            </div>
            
            {/* Dynamic category breakdown */}
            {Object.keys(data?.categoryCounts || {}).length > 0 && (
              <div className="border-t border-border pt-2 mt-2">
                <p className="text-[11px] font-medium text-muted-foreground mb-1">Categories in this bracket:</p>
                <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px]">
                  {Object.entries(data.categoryCounts).map(([cat, count]) => (
                    <div key={cat} className="truncate">
                      <span className="text-muted-foreground">{cat}:</span> <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'text-error bg-error/10 border-error/20';
      case 'high':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'medium':
        return 'text-accent bg-accent/10 border-accent/20';
      case 'low':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case 'critical':
        return 'AlertTriangle';
      case 'high':
        return 'Clock';
      case 'medium':
        return 'Info';
      default:
        return 'CheckCircle';
    }
  };

  if (assets.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
        <div className="text-center py-12">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Age Data Available</h3>
          <p className="text-muted-foreground">Add purchase dates or assets to analyze equipment lifecycle.</p>
        </div>
      </div>
    );
  }

  // Summary tallies
  const totalYoung = ageData.slice(0, 2).reduce((sum, item) => sum + item.count, 0);
  const totalMedium = ageData.slice(2, 3).reduce((sum, item) => sum + item.count, 0);
  const totalOld = ageData.slice(3, 4).reduce((sum, item) => sum + item.count, 0);
  const totalVeryOld = ageData.slice(4).reduce((sum, item) => sum + item.count, 0);
  const totalReplacementCost = replacementRecommendations.reduce((sum, item) => sum + item.estimatedCost, 0);
  const totalReplacementAssets = replacementRecommendations.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Asset Age & Lifecycle Analysis</h3>
          <p className="text-sm text-muted-foreground">Fleet age distribution, depreciation brackets & replacement planning</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={showDepreciation ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowDepreciation(!showDepreciation)}
            iconName="TrendingDown"
            iconPosition="left"
          >
            {showDepreciation ? 'Show Asset Count' : 'Show Depreciation %'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Chart Section */}
        <div className="xl:col-span-2">
          <div className="h-80 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ageData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="ageRange" 
                  tick={{ fontSize: 11 }}
                  stroke="#64748B"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  stroke="#64748B"
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey={showDepreciation ? "depreciation" : "count"} 
                  radius={[4, 4, 0, 0]}
                  name={showDepreciation ? "Depreciation %" : "Asset Count"}
                >
                  {ageData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Age Distribution Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-success/5 rounded-lg border border-success/20">
              <div className="text-2xl font-bold text-success">{totalYoung}</div>
              <div className="text-xs text-muted-foreground font-medium">0-2 Years</div>
              <div className="text-[11px] text-success font-semibold mt-0.5">Prime Condition</div>
            </div>
            <div className="text-center p-3 bg-accent/5 rounded-lg border border-accent/20">
              <div className="text-2xl font-bold text-accent">{totalMedium}</div>
              <div className="text-xs text-muted-foreground font-medium">2-3 Years</div>
              <div className="text-[11px] text-accent font-semibold mt-0.5">Good Working</div>
            </div>
            <div className="text-center p-3 bg-warning/5 rounded-lg border border-warning/20">
              <div className="text-2xl font-bold text-warning">{totalOld}</div>
              <div className="text-xs text-muted-foreground font-medium">3-4 Years</div>
              <div className="text-[11px] text-warning font-semibold mt-0.5">Monitor Closely</div>
            </div>
            <div className="text-center p-3 bg-error/5 rounded-lg border border-error/20">
              <div className="text-2xl font-bold text-error">{totalVeryOld}</div>
              <div className="text-xs text-muted-foreground font-medium">4+ Years</div>
              <div className="text-[11px] text-error font-semibold mt-0.5">Replacement Due</div>
            </div>
          </div>
        </div>

        {/* Replacement Recommendations */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-card-foreground">Lifecycle Replacement Priority</h4>
            <span className="text-xs text-muted-foreground">4+ Years or Past Life</span>
          </div>
          
          <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
            {replacementRecommendations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <Icon name="CheckCircle" size={32} className="text-success mx-auto mb-2 opacity-80" />
                No assets currently due for immediate replacement.
              </div>
            ) : (
              replacementRecommendations.map((item) => (
                <div
                  key={item?.id}
                  className="p-3 border border-border rounded-lg hover:border-primary/50 transition-smooth bg-card"
                >
                  <div className="flex items-start justify-between mb-1.5">
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center space-x-2 mb-0.5">
                        <span className="text-sm font-semibold text-card-foreground truncate">
                          {item?.category}
                        </span>
                        <div className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${getPriorityColor(item?.urgency)}`}>
                          <Icon name={getUrgencyIcon(item?.urgency)} size={11} className="inline mr-1" />
                          {item?.urgency?.toUpperCase()}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {item?.ageRange} • {item?.count} {item?.count === 1 ? 'asset' : 'assets'}
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                    {item?.reason}
                  </p>
                  
                  <div className="flex items-center justify-between pt-1 border-t border-border/40 text-xs">
                    <span className="text-muted-foreground">
                      Estimated Cost: <span className="font-semibold text-card-foreground">
                        {formatINR(item?.estimatedCost, false)}
                      </span>
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Total Replacement Budget */}
          {replacementRecommendations.length > 0 && (
            <div className="p-4 bg-muted/40 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Replacement Budget Needed
                </span>
                <Icon name="IndianRupee" size={16} className="text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold text-card-foreground">
                {formatINR(totalReplacementCost, false)}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Estimated for {totalReplacementAssets} assets requiring lifecycle renewal
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetAgeHistogram;
